import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Container, Typography, Box, Button, Grid, 
  CircularProgress, Alert, Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import axios from 'axios';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import SEO from '../components/common/SEO';
import { getCanonicalUrl } from '../utils/seoConstants';
import StructuredData from '../components/common/StructuredData';
import ThemedRichTextDisplay from '../components/common/ThemedRichTextDisplay';

const TipPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [tip, setTip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTip();
  }, [slug]);

  const fetchTip = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/tips/${slug}`);
      setTip(response.data);
      
      // TODO: Добавить увеличение счетчика просмотров
      // await axios.post(`/api/tips/${response.data.id}/view`);
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching tip:', err);
      if (err.response?.status === 404) {
        setError('Совет не найден');
      } else {
        setError('Не удалось загрузить совет. Пожалуйста, попробуйте позже.');
      }
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return format(new Date(dateString), 'd MMMM yyyy', { locale: ru });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/tips')}
          sx={{ mt: 2 }}
        >
          Вернуться к советам
        </Button>
      </Container>
    );
  }

  if (!tip) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Совет не найден</Alert>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/tips')}
          sx={{ mt: 2 }}
        >
          Вернуться к советам
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* SEO метаданные */}
      <SEO 
        title={tip.metaTitle || tip.title}
        description={tip.metaDescription || tip.excerpt}
        keywords={tip.metaKeywords}
        canonicalUrl={getCanonicalUrl(`/tips/${tip.slug}`)}
        ogImage={tip.featuredImage}
      />
      
      {/* Структурированные данные для поисковых систем */}
      <StructuredData 
        data={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": tip.title,
          "description": tip.excerpt,
          "image": tip.featuredImage ? [tip.featuredImage] : [],
          "datePublished": tip.publishedAt,
          "dateModified": tip.updatedAt,
          "author": {
            "@type": "Person",
            "name": tip.author?.name || "DomikTyumen"
          },
          "publisher": {
            "@type": "Organization",
            "name": "DomikTyumen",
            "logo": {
              "@type": "ImageObject",
              "url": `${window.location.origin}/logo.png`
            }
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": getCanonicalUrl(`/tips/${tip.slug}`)
          }
        }} 
      />
      
      {/* Навигация */}
      <Box sx={{ mb: 4 }}>
        <Button 
          component={Link} 
          to="/tips" 
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 2 }}
        >
          Вернуться к советам
        </Button>
      </Box>
      
      {/* Заголовок и мета-информация */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700, textAlign: 'center' }}>
          <LightbulbIcon sx={{ fontSize: '2rem', mr: 1, color: 'primary.main' }} />
          {tip.title}
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {formatDate(tip.publishedAt)}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {tip.author?.name || 'DomikTyumen'}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <VisibilityIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {tip.viewCount} просмотров
            </Typography>
          </Box>
        </Box>
        
        {/* Краткое описание */}
        {tip.excerpt && (
          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                fontStyle: 'italic',
                color: 'text.secondary',
                fontSize: '1.1rem',
                lineHeight: 1.6,
                px: { xs: 1, sm: 3 },
                borderLeft: (theme) => `3px solid ${theme.palette.primary.main}`,
                pl: 2,
                textAlign: 'left'
              }}
            >
              {tip.excerpt}
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* Изображение */}
      <Box sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
        <img 
          src={tip.featuredImage || '/assets/hero-bg.jpg'} 
          alt={tip.title} 
          style={{ width: '100%', maxHeight: '500px', objectFit: 'cover' }}
          onLoad={() => console.log('Изображение загружено успешно')}
          onError={(e) => {
            console.log('Ошибка загрузки изображения, используем запасное');
            e.target.src = '/assets/placeholder-tips.jpg';
          }}
        />
      </Box>
      
      {/* Содержимое совета */}
      <Box sx={{ mb: 6 }}>
        <ThemedRichTextDisplay content={tip.content} />
      </Box>
      
      {/* Изображения совета */}
      {tip.images && tip.images.length > 0 && (
        <Box sx={{ mb: 6 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            Дополнительные изображения
          </Typography>
          <Grid container spacing={2}>
            {tip.images.map((image) => (
              <Grid item xs={12} sm={6} md={4} key={image.id}>
                <Box 
                  sx={{ 
                    borderRadius: 2, 
                    overflow: 'hidden',
                    height: 200,
                    '& img': {
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease',
                    },
                    '&:hover img': {
                      transform: 'scale(1.05)'
                    }
                  }}
                >
                  <img 
                    src={image.url || image.imageUrl || '/assets/hero-bg.jpg'} 
                    alt={image.altText || image.caption || tip.title}
                    onError={(e) => {
                      console.log('Ошибка загрузки изображения в галерее, используем запасное');
                      e.target.src = '/assets/hero-bg.jpg';
                    }}
                  />
                </Box>
                {image.caption && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {image.caption}
                  </Typography>
                )}
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      
      {/* Навигация внизу */}
      <Divider sx={{ my: 4 }} />
      <Box sx={{ textAlign: 'center' }}>
        <Button 
          component={Link} 
          to="/tips" 
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          size="large"
        >
          Вернуться к советам
        </Button>
      </Box>
    </Container>
  );
};

export default TipPage;
